import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
  ExampleParagraph,
  ParagraphContext,
  StyleProfile,
  StyleRule,
  WritingStyle
} from '@/types/writingStyle';
import { writingStyleService } from '@/services/writingStyleService';
import { writingStyleHelper } from '@/utils/writingStyleHelpers';
import { deserializeWritingStyle, serializeWritingStyle } from '@/utils/writingStyleExport';

const ensureManuscriptId = (value: string | null): string => {
  if (!value) {
    throw new Error('Select or create a manuscript before managing writing style.');
  }
  return value;
};

const countWords = (text: string): number => {
  const normalized = text.trim();
  if (!normalized) {
    return 0;
  }
  return normalized.split(/\s+/u).length;
};

export const useWritingStyleStore = defineStore('writingStyle', () => {
  const writingStyle = ref<WritingStyle | null>(null);
  const activeManuscriptId = ref<string | null>(null);
  const isLoading = ref(false);
  const lastError = ref<string | null>(null);

  const hasWritingStyle = computed(() => writingStyle.value !== null);
  const profile = computed<StyleProfile | null>(() => writingStyle.value?.styleProfile ?? null);
  const exampleParagraphs = computed<ExampleParagraph[]>(() => writingStyle.value?.exampleParagraphs ?? []);
  const styleRules = computed<StyleRule[]>(() => writingStyle.value?.styleRules ?? []);

  const stats = computed(() => {
    const paragraphs = exampleParagraphs.value;
    const totalWords = paragraphs.reduce((sum, paragraph) => sum + countWords(paragraph.text), 0);
    const contexts: Record<ParagraphContext, number> = {
      action: 0,
      description: 0,
      dialogue: 0,
      exposition: 0,
      general: 0,
      introspection: 0
    };
    paragraphs.forEach((paragraph) => {
      const context = paragraph.context ?? 'general';
      contexts[context] = (contexts[context] ?? 0) + 1;
    });
    return {
      paragraphCount: paragraphs.length,
      totalWords,
      contexts
    };
  });

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;
  };

  const loadFromStorage = (): void => {
    const manuscriptId = activeManuscriptId.value;
    if (!manuscriptId) {
      writingStyle.value = null;
      return;
    }
    isLoading.value = true;
    lastError.value = null;
    try {
      const style = writingStyleService.get(manuscriptId);
      writingStyle.value = style ? { ...style } : null;
    } catch (error) {
      console.error('Failed to load writing style.', error);
      lastError.value = 'Unable to load writing style.';
      writingStyle.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const persistToStorage = (): void => {
    if (!writingStyle.value) {
      return;
    }
    writingStyleService.save(writingStyle.value);
  };

  const createWritingStyle = (): WritingStyle => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    const style = writingStyleService.create(manuscriptId);
    writingStyle.value = style;
    persistToStorage();
    return style;
  };

  const updateProfile = (updates: Partial<StyleProfile>): StyleProfile => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    const profile: StyleProfile = {
      ...writingStyle.value.styleProfile,
      ...updates
    };
    writingStyle.value = writingStyleService.updateProfile(writingStyle.value, profile);
    persistToStorage();
    return profile;
  };

  const addExampleParagraph = (input: { label: string; text: string; context?: ParagraphContext; notes?: string; highlightedTechniques?: string[] }): ExampleParagraph => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    const trimmedText = input.text.trim();
    if (trimmedText.length < 100 || trimmedText.length > 4000) {
      throw new Error('Example paragraph must be between 100 and 4000 characters.');
    }
    const paragraph = writingStyleHelper.createEmptyParagraph(writingStyle.value.id);
    const merged: ExampleParagraph = {
      ...paragraph,
      label: input.label.trim(),
      text: trimmedText,
      context: input.context ?? 'general',
      notes: input.notes?.trim(),
      highlightedTechniques: input.highlightedTechniques?.map((value) => value.trim()).filter(Boolean)
    };
    writingStyle.value = writingStyleService.addParagraph(writingStyle.value, merged);
    persistToStorage();
    return merged;
  };

  const updateExampleParagraph = (paragraphId: string, updates: Partial<ExampleParagraph>): ExampleParagraph => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    if (updates.text !== undefined) {
      const trimmed = updates.text.trim();
      if (trimmed.length < 100 || trimmed.length > 4000) {
        throw new Error('Example paragraph must be between 100 and 4000 characters.');
      }
      updates = { ...updates, text: trimmed };
    }
    writingStyle.value = writingStyleService.updateParagraph(writingStyle.value, paragraphId, updates);
    persistToStorage();
    const paragraph = writingStyle.value.exampleParagraphs.find((item) => item.id === paragraphId);
    if (!paragraph) {
      throw new Error('Paragraph not found after update.');
    }
    return paragraph;
  };

  const removeExampleParagraph = (paragraphId: string): void => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    writingStyle.value = writingStyleService.removeParagraph(writingStyle.value, paragraphId);
    persistToStorage();
  };

  const addStyleRule = (input: { rule: string; rationale: string; severity: StyleRule['severity']; goodExample: string; badExample: string; notes?: string }): StyleRule => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    const now = new Date().toISOString();
    const rule: StyleRule = {
      id: writingStyleHelper.generateId(),
      styleId: writingStyle.value.id,
      rule: input.rule.trim(),
      rationale: input.rationale.trim(),
      severity: input.severity,
      goodExample: input.goodExample.trim(),
      badExample: input.badExample.trim(),
      notes: input.notes?.trim() ?? '',
      uiState: {
        isExpanded: false
      },
      createdAt: now,
      updatedAt: now
    };
    writingStyle.value = writingStyleService.addRule(writingStyle.value, rule);
    persistToStorage();
    return rule;
  };

  const updateStyleRule = (ruleId: string, updates: Partial<StyleRule>): StyleRule => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    writingStyle.value = writingStyleService.updateRule(writingStyle.value, ruleId, {
      ...updates,
      rule: updates.rule?.trim(),
      rationale: updates.rationale?.trim(),
      goodExample: updates.goodExample?.trim(),
      badExample: updates.badExample?.trim(),
      notes: updates.notes?.trim()
    });
    persistToStorage();
    const rule = writingStyle.value.styleRules.find((item) => item.id === ruleId);
    if (!rule) {
      throw new Error('Style rule not found after update.');
    }
    return rule;
  };

  const removeStyleRule = (ruleId: string): void => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    writingStyle.value = writingStyleService.removeRule(writingStyle.value, ruleId);
    persistToStorage();
  };

  const exportWritingStyle = (): string => {
    if (!writingStyle.value) {
      throw new Error('Create the writing style profile first.');
    }
    return serializeWritingStyle(writingStyle.value);
  };

  const importWritingStyle = (json: string): void => {
    const manuscriptId = ensureManuscriptId(activeManuscriptId.value);
    const imported = deserializeWritingStyle(manuscriptId, json);
    writingStyle.value = imported;
    persistToStorage();
  };

  const clearState = (): void => {
    writingStyle.value = null;
    lastError.value = null;
  };

  watch(
    activeManuscriptId,
    () => {
      loadFromStorage();
    },
    { immediate: true }
  );

  return {
    writingStyle,
    profile,
    exampleParagraphs,
    styleRules,
    stats,
    hasWritingStyle,
    isLoading,
    lastError,
    setActiveManuscript,
    createWritingStyle,
    updateProfile,
    addExampleParagraph,
    updateExampleParagraph,
    removeExampleParagraph,
    addStyleRule,
    updateStyleRule,
    removeStyleRule,
    exportWritingStyle,
    importWritingStyle,
    clearState
  };
});

